import { apiErrorMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { CheckIcon, MinusIcon, WarningIcon } from '@/components/common/icons';
import { CARD_FLOATING, SCREEN } from '@/components/common/surfaces';
import { useBackendLabel } from '@/components/documents/labels';
import type { DetectionStatusModel, DocumentModel, StepModel } from '@/types/documents.types';
import { stepLabelKey } from '@/utils/categories';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DetectionProgressProps {
  document: DocumentModel;
  /** How many categories the run detects (the catalogue), for the subtitle. */
  categoryCount: number;
  /** The latest poll, or `null` until the first one answers. */
  status: DetectionStatusModel | null;
  /** A transient polling failure, shown without ending the view. */
  error: string | null;
}

/** Pages a done step could not analyse; empty for any other state and when the backend sends no list. */
const skippedPagesOf = (step: StepModel): number[] => (step.state === 'done' ? (step.skipped_pages ?? []) : []);

/**
 * The prototype's 18 px step marker: green check when done, cyan spinner while running, grey ring before.
 * A done step that skipped pages is neither a success nor a failure, so it gets the amber warning sign.
 */
const StepMarker: FC<{ step: StepModel }> = ({ step }) => {
  if (skippedPagesOf(step).length > 0) {
    return <WarningIcon size={18} strokeWidth={2.2} className="flex-none text-warn" />;
  }
  if (step.state === 'done') {
    return (
      <div className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-ok text-white">
        <CheckIcon size={10} strokeWidth={4} />
      </div>
    );
  }
  if (step.state === 'failed') {
    return (
      <div className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-danger text-white">
        <MinusIcon size={10} strokeWidth={4} />
      </div>
    );
  }
  if (step.state === 'running') {
    return (
      <div className="h-[18px] w-[18px] flex-none animate-[spin_.9s_linear_infinite] rounded-full border-2 border-accent border-t-transparent" />
    );
  }
  return <div className="h-[18px] w-[18px] flex-none rounded-full border-2 border-control bg-white" />;
};

/** Determinate bar under a running page-by-page step (the LLM), filled by pages analysed so far. */
const PageProgress: FC<{ step: StepModel }> = ({ step }) => {
  const done = step.pages_done ?? 0;
  const total = step.pages_total ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      className="ml-[30px] h-1.5 basis-full overflow-hidden rounded-full bg-hairline"
    >
      <div
        className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
};

const LABEL_CLASSES: Record<StepModel['state'], string> = {
  done: 'font-normal text-ink',
  running: 'font-semibold text-primary',
  pending: 'font-normal text-off',
  skipped: 'font-normal text-off',
  failed: 'font-normal text-danger',
};

/**
 * The prototype's progress card: spinner, the file line, the backend's steps with the count each
 * produced, the in-memory note and "← Zrušit a vrátit se".
 *
 * Cancelling is a request: the backend stops the job at its next checkpoint and the polling picks
 * the `cancelled` status up, which returns the upload screen to the document.
 */
export const DetectionProgress: FC<DetectionProgressProps> = ({ document, categoryCount, status, error }) => {
  const { t } = useTranslation();
  const backendLabel = useBackendLabel();
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = async () => {
    setIsCancelling(true);
    setCancelError(null);
    try {
      // No reset on success: the button stays busy until the polling reports `cancelled`.
      await documentsApi.cancelDetection(document.id);
    } catch (err) {
      setCancelError(apiErrorMessage(err, t('documents.detection.errors.cancelFailed')));
      setIsCancelling(false);
    }
  };

  const countOf = (step: StepModel): string => {
    if (step.state === 'skipped') return t('documents.detection.skipped');
    if (step.state === 'running' && (step.pages_total ?? 0) > 0)
      return t('documents.detection.pagesProgress', {
        done: step.pages_done ?? 0,
        total: step.pages_total,
      });
    if (step.state !== 'done') return '';
    return step.key === 'parse' ? t('documents.card.pages', { count: step.count }) : String(step.count);
  };

  return (
    <div className={`${SCREEN} items-center justify-center px-7 pb-20 pt-10`}>
      <div className={`${CARD_FLOATING} w-full max-w-[520px] px-9 pb-[30px] pt-9`}>
        <div className="mb-[22px] h-[54px] w-[54px] animate-spin rounded-full border-4 border-accent-soft border-t-accent" />
        <h2 className="m-0 mb-1.5 text-[19px] font-bold text-primary">{t('documents.detection.title')}</h2>
        <div className="mb-6 text-[12.5px] text-subtle">
          {document.filename} · {t('documents.card.pages', { count: document.page_count })}
          {categoryCount > 0 ? ` · ${t('documents.detection.categories', { count: categoryCount })}` : ''}
        </div>
        <ol className="m-0 flex list-none flex-col p-0" aria-live="polite">
          {(status?.steps ?? []).map((step) => {
            const skipped = skippedPagesOf(step);
            return (
              <li
                key={step.key}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-hairline py-[9px]"
              >
                <StepMarker step={step} />
                <span className={`text-[13px] ${LABEL_CLASSES[step.state]}`}>
                  {backendLabel(step.label_cs, stepLabelKey(step.key))}
                  {/* The state is read out only; skipped pages turn it into a visible line under the label. */}
                  <span className={skipped.length > 0 ? 'block text-[11.5px] text-warn' : 'sr-only'}>
                    {skipped.length > 0
                      ? t('documents.detection.skippedPages', { count: skipped.length, pages: skipped.join(', ') })
                      : t(`documents.detection.state.${step.state}`)}
                  </span>
                </span>
                <span className="flex-1" />
                <span className="text-[11.5px] font-semibold text-subtle">{countOf(step)}</span>
                {step.state === 'running' && (step.pages_total ?? 0) > 0 ? <PageProgress step={step} /> : null}
              </li>
            );
          })}
        </ol>
        {status?.steps.find((step) => step.error)?.error || cancelError || error ? (
          <p role="alert" className="m-0 mt-3 text-xs text-danger">
            {cancelError ?? error ?? status?.steps.find((step) => step.error)?.error}
          </p>
        ) : null}
        <div className="mb-[18px] mt-4 border-t border-hairline pt-4 text-[11.5px] leading-[1.55] text-faint">
          {t('documents.detection.footer')}
        </div>
        <button
          type="button"
          disabled={isCancelling}
          onClick={handleCancel}
          className="cursor-pointer rounded border border-control bg-white px-4 py-2.5 text-[13px] font-semibold text-muted hover:border-primary hover:text-primary disabled:cursor-progress disabled:opacity-60"
        >
          {isCancelling ? t('documents.detection.cancelling') : t('documents.detection.cancel')}
        </button>
      </div>
    </div>
  );
};
